import React, { useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';

import { SupportedCurrency } from '../../../../domain/currency';
import { StatsEntity } from '../../../../domain/stats';
import { ExportFormat, StatType, statTypeTranslations } from '../../../../domain/statsExport';
import { buildOrderMonthYearRange, StatsFilters } from '../../../../domain/statsFilters';
import {
    useStatsExportRepository,
} from '../../../../repository/stats-export/useStatsExportRepository';
import { ExportFiltersSummary } from './ExportFiltersSummary';

// Component that displays detailed information about each stat type

interface ExportStatsModalProps {
    stats: StatsEntity;
    show: boolean;
    onHide: () => void;
    filters: StatsFilters;
    selectedCurrency: SupportedCurrency;
}

export const ExportStatsModal: React.FC<ExportStatsModalProps> = ({
    stats,
    show,
    onHide,
    filters,
    selectedCurrency,
}) => {
    const [exportFormat, setExportFormat] = useState<ExportFormat>('xlsx');
    const [selectedStatTypes, setSelectedStatTypes] = useState<StatType[]>(['profit', 'contractors', 'transport-orders-status', 'creators']);
    const [isExporting, setIsExporting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const { exportStats } = useStatsExportRepository();

    const handleExportFormatChange = (format: ExportFormat) => {
        setExportFormat(format);

        if (format === 'csv') {
            setSelectedStatTypes(['profit']);
        } else {
            setSelectedStatTypes(['profit', 'contractors', 'transport-orders-status', 'creators']);
        }
    };

    const handleStatTypeChange = (statType: StatType, checked: boolean) => {
        if (exportFormat === 'csv') {
            setSelectedStatTypes([statType]);
        } else {
            if (checked) {
                setSelectedStatTypes([...selectedStatTypes, statType]);
            } else {
                setSelectedStatTypes(selectedStatTypes.filter(type => type !== statType));
            }
        }
    };

    const handleExport = async () => {
        try {
            setIsExporting(true);
            setError(null);

            // Convert any date format to a monthRange for consistency
            const monthRangeFilters = {
                ...filters,
                timePeriodSelector: 'month-range' as const,
                monthYearRange: buildOrderMonthYearRange(filters),
            };

            await exportStats(exportFormat, selectedStatTypes, monthRangeFilters, selectedCurrency);

            onHide();
        } catch (err) {
            setError('Nie udało się wyeksportować statystyk. Spróbuj ponownie.');
            console.error('Export error:', err);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>Eksport statystyk</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <ExportFiltersSummary
                        filters={filters}
                        selectedCurrency={selectedCurrency}
                        selectedContractor={stats.contractorsStats.find(contractor => contractor.id === filters.contractor?.id)}
                    />

                    <Form.Group className="mb-3">
                        <Form.Label>Format</Form.Label>
                        <div>
                            <Form.Check
                                inline
                                type="radio"
                                id="export-format-xlsx"
                                label="XLSX"
                                checked={exportFormat === 'xlsx'}
                                onChange={() => handleExportFormatChange('xlsx')}
                            />
                            <Form.Check
                                inline
                                type="radio"
                                id="export-format-csv"
                                label="CSV"
                                checked={exportFormat === 'csv'}
                                onChange={() => handleExportFormatChange('csv')}
                            />
                        </div>
                        <small className="text-muted mt-1 d-block">
                            {exportFormat === 'csv' ? (
                                'Format CSV pozwala na eksport jednego typu statystyk.'
                            ) : (
                                'Format XLSX pozwala na eksport wielu typów statystyk w osobnych arkuszach.'
                            )}
                        </small>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Statystyki do eksportu</Form.Label>
                        <div>
                            {exportFormat === 'csv' ? (
                                // Radio buttons for CSV (single selection)
                                <>
                                    <Form.Check
                                        type="radio"
                                        id="stat-type-creators"
                                        label={statTypeTranslations['creators']}
                                        checked={selectedStatTypes.includes('creators')}
                                        onChange={(e) => handleStatTypeChange('creators', e.target.checked)}
                                    />
                                    <Form.Check
                                        type="radio"
                                        id="stat-type-contractors"
                                        label={statTypeTranslations['contractors']}
                                        checked={selectedStatTypes.includes('contractors')}
                                        onChange={(e) => handleStatTypeChange('contractors', e.target.checked)}
                                    />
                                    <Form.Check
                                        type="radio"
                                        id="stat-type-transport-orders-status"
                                        label={statTypeTranslations['transport-orders-status']}
                                        checked={selectedStatTypes.includes('transport-orders-status')}
                                        onChange={(e) => handleStatTypeChange('transport-orders-status', e.target.checked)}
                                    />
                                    <Form.Check
                                        type="radio"
                                        id="stat-type-profit"
                                        label={statTypeTranslations['profit']}
                                        checked={selectedStatTypes.includes('profit')}
                                        onChange={(e) => handleStatTypeChange('profit', e.target.checked)}
                                    />
                                </>
                            ) : (
                                <>
                                    <Form.Check
                                        type="checkbox"
                                        id="stat-type-creators"
                                        label={statTypeTranslations['creators']}
                                        checked={selectedStatTypes.includes('creators')}
                                        onChange={(e) => handleStatTypeChange('creators', e.target.checked)}
                                    />
                                    <Form.Check
                                        type="checkbox"
                                        id="stat-type-contractors"
                                        label={statTypeTranslations['contractors']}
                                        checked={selectedStatTypes.includes('contractors')}
                                        onChange={(e) => handleStatTypeChange('contractors', e.target.checked)}
                                    />
                                    <Form.Check
                                        type="checkbox"
                                        id="stat-type-transport-orders-status"
                                        label={statTypeTranslations['transport-orders-status']}
                                        checked={selectedStatTypes.includes('transport-orders-status')}
                                        onChange={(e) => handleStatTypeChange('transport-orders-status', e.target.checked)}
                                    />
                                    <Form.Check
                                        type="checkbox"
                                        id="stat-type-profit"
                                        label={statTypeTranslations['profit']}
                                        checked={selectedStatTypes.includes('profit')}
                                        onChange={(e) => handleStatTypeChange('profit', e.target.checked)}
                                    />
                                </>
                            )}
                        </div>
                    </Form.Group>

                    {error && (
                        <div className="alert alert-danger" role="alert">
                            {error}
                        </div>
                    )}
                    {selectedStatTypes.length === 0 && (
                        <div className="alert alert-warning" role="alert">
                            Wybierz co najmniej jeden typ statystyk do eksportu.
                        </div>
                    )}
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Anuluj
                </Button>
                <Button
                    variant="primary"
                    onClick={handleExport}
                    disabled={isExporting || selectedStatTypes.length === 0}
                >
                    {isExporting ? 'Eksportowanie...' : 'Eksportuj'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
